'use client'

import { forwardRef, useEffect, useState } from 'react'

import { clsx } from 'clsx'
import { useAtom } from 'jotai'
import { useParams } from 'next/navigation'

import { Badge } from '@dfds-pax/shared-components/client'

import getSeabookResource from '@/app/[locale]/[ferry]/_actions/getSeabookResource'
import { useBasket } from '@/providers/basket-context'
import { selectedOfferAtom } from '@/states/selectionAtom'

import { useTranslation } from '@/hooks/useDebugTranslations'

import Tooltip from '../shared/Tooltip'

interface Props {
  description?: string
  offer: {
    code: string
    description: string
    price: number
  }
}

// eslint-disable-next-line react/display-name
const OfferBadge = forwardRef<HTMLDivElement, { disabled: boolean; title?: string }>(({ disabled, title }, ref) => {
  return (
    <div className="w-full" ref={ref}>
      <Badge
        className={clsx(
          'w-full! max-w-full text-center whitespace-normal',
          disabled &&
            'bg-semantic-global-disabled-light! text-semantic-global-disabled-darker! [&_svg]:text-semantic-global-disabled-darker!'
        )}
        emphasis="high"
        label={title}
        showIcon
        size="sm"
        status={disabled ? 'neutral' : 'success'}
      />
    </div>
  )
})

export const OfferInfo: React.FC<Props> = ({ description, offer }) => {
  const { t } = useTranslation()
  const [selectedOffer] = useAtom(selectedOfferAtom)
  const [offerTitle, setOfferTitle] = useState<string>()
  const { locale } = useParams()

  const { basket } = useBasket()

  useEffect(() => {
    if (description !== undefined) return
    let active = true
    setOfferTitle(undefined) // Hide the previous offer's title while the new lookup is in flight.
    const loadTitle = async () => {
      const title = offer.code ? await getSeabookResource(locale as string, offer.code, 'offer') : undefined
      if (active) setOfferTitle(title)
    }
    loadTitle()
    return () => {
      active = false
    }
  }, [offer.code, locale, description])

  const showTooltip = basket?.departures.length === 1 && selectedOffer.offerRLSHours && selectedOffer.offerRLSHours > 0

  // Render the badge only once the title has resolved, to avoid flashing an empty badge.
  const title = description ?? offerTitle
  if (!title) return null

  return (
    <section id="offer-summary">
      {showTooltip ? (
        <Tooltip adjustToParent={true} text={`${t('OFFER-REQUIRED-TITLE')}. ${t('OFFER-REQUIRED-DESCRIPTION')}`}>
          <OfferBadge disabled={showTooltip} title={title} />
        </Tooltip>
      ) : (
        <OfferBadge disabled={false} title={title} />
      )}
    </section>
  )
}
